import { normalizeNoteHtml } from '../../../lib/noteHtml/normalize';

// Pure DOM helpers for the contenteditable note editor. No execCommand here —
// everything below is a plain Range/DOM manipulation so it can run (and be
// asserted against) inside jsdom. The one browser behavior this module leans
// on is Range.extractContents()'s spec'd "clone partially-selected ancestors"
// semantics: splitting a Range whose boundary falls inside e.g. <strong> text
// naturally re-wraps only the extracted portion in <strong> — that is how
// "moved text keeps its own tags" falls out for free below.

const BLOCK_TAGS = new Set(['P', 'LI']);

// Only P and LI are ever direct containers of inline content/marks in the
// canonical subset, and they never nest, so the first P/LI ancestor found by
// walking up from anywhere in the selection IS the enclosing block.
const closestBlock = (node, rootEl) => {
    let el = node.nodeType === 3 ? node.parentNode : node;
    while (el && el !== rootEl) {
        if (BLOCK_TAGS.has(el.tagName)) return el;
        el = el.parentNode;
    }
    return null;
};

const isEmptyBlock = (block) => block.textContent === '';

// Range.extractContents(), when a boundary point falls exactly at the end of
// a Text node, still clones that node into the result (with empty data)
// rather than omitting it — spec'd CharacterData-boundary behavior, not a
// bug. Left in place, a lone empty Text node would make hasChildNodes()
// report "has content" for a block that is actually empty, so the <br>
// placeholder step below would be skipped. Drop those before checking.
const stripEmptyTextNodes = (el) => {
    Array.from(el.childNodes).forEach((node) => {
        if (node.nodeType === 3 && node.nodeValue === '') el.removeChild(node);
    });
};

// Same boundary behavior, one level up: when a split point falls exactly at
// the end of text inside a nested inline mark (e.g. "...ab<strong>c|</strong>"
// with the caret after "c"), extractContents() clones the *ancestor* chain
// (<strong>, and anything nested inside it) as well as the text — producing
// an EMPTY styled element (or nest of them) rather than an empty text node.
// stripEmptyTextNodes only looks at el's direct children, so it never sees
// these (they're wrapped). Recurse first so a multi-level nest (<em><u></u>
// </em>) empties from the inside out, then drop any element whose entire
// subtree — recursively — has no text. <br> is deliberately exempt: it's a
// real placeholder marker, not leftover wrapper debris, and has no text
// content by design.
const stripEmptyInlineElements = (el) => {
    Array.from(el.childNodes).forEach((node) => {
        if (node.nodeType !== 1 || node.tagName === 'BR') return;
        stripEmptyInlineElements(node);
        if (node.textContent === '') el.removeChild(node);
    });
};

// True when (container, offset) is the first possible caret position inside
// block — i.e. nothing but the block's own start precedes it. Built as "is
// the text between block-start and here empty" rather than a raw offset
// check so it's correct regardless of how many inline-tag boundaries sit
// between the two points.
const isAtBlockStart = (block, container, offset) => {
    const range = document.createRange();
    range.setStart(block, 0);
    range.setEnd(container, offset);
    return range.toString() === '';
};

const placeCaretAtStart = (block, selection) => {
    const range = document.createRange();
    range.setStart(block, 0);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};

// Removes everything from (container, offset) to the end of `block`, keeping
// whatever inline tags that content had. Returns the detached fragment.
const extractTailFrom = (block, container, offset) => {
    const range = document.createRange();
    range.setStart(container, offset);
    range.setEnd(block, block.childNodes.length);
    return range.extractContents();
};

// Splits `block` at (container, offset): a same-tag sibling is inserted right
// after it, holding whatever was extracted from the caret onward. Used for
// both mid-<p> Enter and mid-<li> (list-continue) Enter — the only
// difference between the two is block.tagName, which this already preserves.
const splitBlock = (block, container, offset) => {
    const fragment = extractTailFrom(block, container, offset);
    const newBlock = document.createElement(block.tagName);
    newBlock.appendChild(fragment);
    stripEmptyTextNodes(newBlock);
    stripEmptyTextNodes(block);
    // Element-level counterpart of the text-node strip above — must run
    // before the hasChildNodes() checks below, or a caret that split right
    // at the end of a styled run (e.g. "...c" inside <strong>) leaves an
    // empty <strong></strong> that reads as "has content" and silently
    // skips the <br> placeholder, producing a zero-height row.
    stripEmptyInlineElements(newBlock);
    stripEmptyInlineElements(block);
    if (!newBlock.hasChildNodes()) newBlock.appendChild(document.createElement('br'));
    if (!block.hasChildNodes()) block.appendChild(document.createElement('br'));
    block.parentNode.insertBefore(newBlock, block.nextSibling);
    return newBlock;
};

// Pulls `li` out of its <ul>/<ol>, replacing it in the flow with `newEl`
// (already-built <p>). Items before `li` stay in the original list; items
// after it move into a fresh same-kind list placed right after `newEl`; the
// original list is dropped entirely if `li` was its only/first item.
const spliceListWithReplacement = (li, newEl) => {
    const list = li.parentNode;
    const items = Array.from(list.children);
    const idx = items.indexOf(li);
    const before = items.slice(0, idx);
    const after = items.slice(idx + 1);
    const parent = list.parentNode;

    parent.insertBefore(newEl, list.nextSibling);
    if (after.length) {
        const newList = document.createElement(list.tagName);
        after.forEach((item) => newList.appendChild(item));
        parent.insertBefore(newList, newEl.nextSibling);
    }
    li.remove();
    if (!before.length) list.remove();
};

// Enter on an empty <li>: exits the list into a clean <p><br></p>, splitting
// the list around it when the item was interior.
const exitEmptyListItem = (li) => {
    const p = document.createElement('p');
    p.appendChild(document.createElement('br'));
    spliceListWithReplacement(li, p);
    return p;
};

// Backspace at offset 0 of a <li>: unwraps the WHOLE item (its own content,
// moved as-is — no caret-position splitting) into a <p>, splitting the list
// around it when the item was interior. Not susceptible to the empty-
// styled-remnant class of bug splitBlock/handlePasteText have: this moves
// every existing child wholesale (no Range.extractContents() boundary
// crossing an inline mark), so it can never manufacture a new empty clone.
const unwrapListItemToParagraph = (li) => {
    const p = document.createElement('p');
    Array.from(li.childNodes).forEach((node) => p.appendChild(node));
    if (!p.hasChildNodes()) p.appendChild(document.createElement('br'));
    spliceListWithReplacement(li, p);
    return p;
};

// normalize, then make the canonical subset editable: a browser needs a <br>
// inside an otherwise-empty block to render it as a line and let the caret
// land there, so every empty <p></p> (and <li></li>, same reasoning) gets one.
export const editorHtmlFromText = (text) => normalizeNoteHtml(text)
    .replace(/<p><\/p>/g, '<p><br></p>')
    .replace(/<li><\/li>/g, '<li><br></li>');

// Reads the live editor DOM back out to the canonical stored subset. The
// normalizer already drops a lone trailing <br> placeholder (BR-splitting of
// an empty tail yields an empty block, which the trailing-BR rule discards),
// so '<p><br></p>' round-trips to '<p></p>'.
export const textFromEditorDom = (rootEl) => normalizeNoteHtml(rootEl.innerHTML);

// Spec Enter rules. Returns true when it handled the key (caller calls
// e.preventDefault()), false to let the browser's default Enter proceed
// (should not happen in practice — every caret position is inside some P/LI
// once the editor is mounted via editorHtmlFromText).
export const handleEnter = (rootEl, selection) => {
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    if (!rootEl.contains(range.commonAncestorContainer)) return false;
    if (!range.collapsed) range.deleteContents();

    const { startContainer, startOffset } = range;
    const block = closestBlock(startContainer, rootEl);
    if (!block) return false;

    if (block.tagName === 'LI' && isEmptyBlock(block)) {
        const newP = exitEmptyListItem(block);
        placeCaretAtStart(newP, selection);
        return true;
    }

    const newBlock = splitBlock(block, startContainer, startOffset);
    placeCaretAtStart(newBlock, selection);
    return true;
};

// Spec rule 7: collapsed caret at offset 0 of a <li> unwraps that item to a
// <p> (splitting the list when interior). Returns false for anything else so
// normal Backspace proceeds (deleting a character, merging with the previous
// block, etc.).
export const handleBackspaceAtListStart = (rootEl, selection) => {
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    if (!range.collapsed) return false;
    if (!rootEl.contains(range.commonAncestorContainer)) return false;

    const { startContainer, startOffset } = range;
    const block = closestBlock(startContainer, rootEl);
    if (!block || block.tagName !== 'LI') return false;
    if (!isAtBlockStart(block, startContainer, startOffset)) return false;

    const newP = unwrapListItemToParagraph(block);
    placeCaretAtStart(newP, selection);
    return true;
};

// Plain-text-only paste: each '\n'-separated line becomes its own block at
// the caret (same-tag as the block being pasted into, so pasting inside a
// <li> continues the list). The first line joins the existing block up to
// the caret; the last line is followed by whatever was after the caret
// (which keeps its own tags, same extraction as handleEnter).
export const handlePasteText = (rootEl, selection, text) => {
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!rootEl.contains(range.commonAncestorContainer)) return;
    if (!range.collapsed) range.deleteContents();

    const { startContainer, startOffset } = range;
    const lines = String(text ?? '').split('\n');

    if (lines.length === 1) {
        const insertRange = document.createRange();
        insertRange.setStart(startContainer, startOffset);
        insertRange.collapse(true);
        const node = document.createTextNode(lines[0]);
        insertRange.insertNode(node);
        const caret = document.createRange();
        caret.setStartAfter(node);
        caret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(caret);
        return;
    }

    const block = closestBlock(startContainer, rootEl);
    if (!block) return;

    const tailFragment = extractTailFrom(block, startContainer, startOffset);

    const insertRange = document.createRange();
    insertRange.setStart(startContainer, startOffset);
    insertRange.collapse(true);
    insertRange.insertNode(document.createTextNode(lines[0]));

    const tag = block.tagName;
    let insertAfter = block;
    for (let i = 1; i < lines.length - 1; i += 1) {
        const midBlock = document.createElement(tag);
        midBlock.appendChild(document.createTextNode(lines[i]));
        insertAfter.parentNode.insertBefore(midBlock, insertAfter.nextSibling);
        insertAfter = midBlock;
    }

    const tailBlock = document.createElement(tag);
    const lastLine = lines[lines.length - 1];
    let caretOffset = 0;
    if (lastLine) {
        tailBlock.appendChild(document.createTextNode(lastLine));
        caretOffset = 1;
    }
    tailBlock.appendChild(tailFragment);
    stripEmptyTextNodes(tailBlock);
    // Same extraction (extractTailFrom) as splitBlock, same boundary case:
    // pasting multiple lines with the caret at the end of a styled run can
    // leave an empty styled wrapper in tailFragment. Only matters when
    // lastLine is itself empty (otherwise tailBlock already has real text
    // and isn't at risk of being skipped below), but is cheap and always
    // correct to run regardless.
    stripEmptyInlineElements(tailBlock);
    if (!tailBlock.hasChildNodes()) tailBlock.appendChild(document.createElement('br'));
    insertAfter.parentNode.insertBefore(tailBlock, insertAfter.nextSibling);

    const caret = document.createRange();
    caret.setStart(tailBlock, caretOffset);
    caret.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caret);
};

// --- Pending typing style (spec rule 5.A/B/C) ------------------------------
//
// A collapsed-caret style click no longer rewrites the note's text; it arms
// a "pending" set of style toggles that the NEXT typed character(s) carry,
// layered on top of whatever marks already surround the caret (see
// effectiveStyleMarks below — this is what makes "click Bold while the caret
// is already inside bold text" mean "stop bold from here on"). This is
// state EditableNote owns itself (a ref, not the DOM/execCommand) because
// the "stays armed through a no-op Backspace" rule has no native equivalent.
//
// pendingStyleReducer is a pure (state, event) -> state reducer, independent
// of the DOM, so the exact user scenarios from the spec can be unit-tested
// without any selection/contenteditable glue. Wiring it up to real keydown/
// beforeinput/selectionchange events lives in EditableNote.jsx and is
// manual-QA territory (jsdom has no execCommand and no realistic native
// typing pipeline to drive beforeinput the way a real browser would).
export const STYLE_MARK_KEYS = ['bold', 'italic', 'underline', 'strike'];

export const createPendingStyleState = () => ({ pending: {}, typedCount: 0 });

export const isPendingStyleArmed = (state) => Object.keys(state.pending).length > 0;

export const pendingStyleReducer = (state, event) => {
    switch (event.type) {
        case 'armToggle': {
            const pending = { ...state.pending };
            if (pending[event.mark]) delete pending[event.mark]; else pending[event.mark] = true;
            const armed = Object.keys(pending).length > 0;
            // Toggling the pending set back to empty is a full disarm — the
            // typedCount from whatever was armed before no longer applies.
            return { pending, typedCount: armed ? state.typedCount : 0 };
        }
        case 'typed':
            if (!isPendingStyleArmed(state)) return state;
            return { ...state, typedCount: state.typedCount + (event.count || 1) };
        case 'backspace': {
            if (!isPendingStyleArmed(state)) return state;
            // Nothing typed under the pending style yet: the deleted
            // character predates it, so the delete proceeds (natively, by
            // the caller) but pending itself is untouched (spec: "stays
            // armed").
            if (state.typedCount === 0) return state;
            // `count` is the number of UTF-16 units ONE native Backspace
            // actually removed — not always 1. Deleting a single emoji/
            // astral character removes a surrogate PAIR (2 units) in one
            // keystroke; a flat "decrement by 1" would leave a phantom
            // typedCount that then wrongly extends the keep-armed window
            // onto text that predates the pending style. Missing/omitted
            // (older callers, existing tests) defaults to 1. Clamped at 0:
            // an overshoot (count larger than what's left) is treated the
            // same as landing on exactly 0 — everything typed under
            // pending is gone, so disarm rather than going negative.
            const count = event.count == null ? 1 : event.count;
            const typedCount = Math.max(0, state.typedCount - count);
            return typedCount === 0 ? createPendingStyleState() : { ...state, typedCount };
        }
        // A Backspace that unwraps a <li> to a <p> (caret at a list item's
        // start, see handleBackspaceAtListStart) is a STRUCTURAL DOM edit,
        // not a plain character delete — there is no "count of units
        // removed from typed text" for it to decrement, since the block's
        // own text is untouched (only its tag changes). Spec-literal
        // reading, per review: with nothing typed under the pending style
        // yet (typedCount === 0) the unwrap proceeds and pending STAYS
        // armed, exactly as a no-op character Backspace does; once
        // something HAS been typed under it (typedCount > 0), the
        // structural edit is not a typing continuation and disarms, same
        // as Enter/execList.
        case 'backspaceStructural': {
            if (!isPendingStyleArmed(state)) return state;
            if (state.typedCount === 0) return state;
            return createPendingStyleState();
        }
        case 'enter':
        case 'caretMoved':
        case 'selectionMade':
            return createPendingStyleState();
        default:
            return state;
    }
};

// Baseline (marksAtNode's output at the caret) XOR pending toggles == the
// style the NEXT typed character(s) will carry, and what a collapsed-caret
// menu indicator should show (spec rule 5.D). Only the 4 toggleable style
// marks participate — link is never a typing style (rule 4: link only ever
// applies over a selection).
export const effectiveStyleMarks = (baselineMarks, pendingMarks) => {
    const out = {};
    STYLE_MARK_KEYS.forEach((key) => { out[key] = !!baselineMarks[key] !== !!pendingMarks[key]; });
    return out;
};

// How many UTF-16 units ONE native Backspace at the current collapsed
// caret is about to remove — feeds both the 'backspace' reducer event's
// `count` (see pendingStyleReducer) and EditableNote's self-move-latch
// gating (a Backspace that removes nothing should neither dispatch a
// reducer event nor arm the latch, since no selectionchange will follow to
// consume it — see EditableNote.jsx's handleBeforeInput).
//
// This is the jsdom-safe FALLBACK: the real browser's own `beforeinput`
// target range (`event.getTargetRanges()[0]`, read in EditableNote.jsx) is
// authoritative when available and is preferred over this — it reflects
// whatever the browser actually resolved, including full grapheme clusters
// beyond a bare surrogate pair. This only covers the two cases that matter
// without it: a lone high/low surrogate pair immediately before the caret
// (an emoji/astral character deleted as one atomic unit -> 2), and the
// caret sitting at the very start of the note with nothing before it to
// delete at all (a true no-op -> 0). Anything else -> 1.
export const backspaceDeleteCount = (rootEl, selection) => {
    if (!selection || selection.rangeCount === 0) return 1;
    const range = selection.getRangeAt(0);
    if (!rootEl.contains(range.commonAncestorContainer) || !range.collapsed) return 1;
    const { startContainer, startOffset } = range;

    if (startContainer.nodeType === 3 && startOffset >= 2) {
        const text = startContainer.nodeValue;
        const low = text.charCodeAt(startOffset - 1);
        const high = text.charCodeAt(startOffset - 2);
        if (high >= 0xD800 && high <= 0xDBFF && low >= 0xDC00 && low <= 0xDFFF) return 2;
    }

    const probe = document.createRange();
    probe.setStart(rootEl, 0);
    probe.setEnd(startContainer, startOffset);
    if (probe.toString() === '') return 0;

    return 1;
};

// Canonical nesting order (matches serializeBlocks' wrapRun): strong is
// outermost among the style tags, then em, then u, then s innermost.
const wrapWithMarks = (node, marks) => {
    let wrapped = node;
    if (marks.strike) { const el = document.createElement('s'); el.appendChild(wrapped); wrapped = el; }
    if (marks.underline) { const el = document.createElement('u'); el.appendChild(wrapped); wrapped = el; }
    if (marks.italic) { const el = document.createElement('em'); el.appendChild(wrapped); wrapped = el; }
    if (marks.bold) { const el = document.createElement('strong'); el.appendChild(wrapped); wrapped = el; }
    return wrapped;
};

// Inserts `text` at the collapsed caret, tagged with EXACTLY `marks`
// (present-only-when-true; bold/italic/underline/strike) regardless of what
// tags happen to already surround the caret — this is what lets a toggled-
// OFF mark produce plain text with no wrapping tag even mid-run. Reuses the
// same extractTailFrom/strip-empty machinery as splitBlock: everything from
// the caret to the end of its block is peeled off first (keeping its own
// tags, exactly like moved text on Enter), the new marks-wrapped text is
// appended in its place, then the peeled tail is appended after it.
//
// No innerHTML rewrite — this is surgical DOM insertion (a handful of
// appendChild calls), so it never touches, and never disturbs, native
// undo/redo the way canonicalizeInPlace's innerHTML swap would.
export const insertStyledText = (rootEl, selection, text, marks) => {
    if (!text) return;
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!rootEl.contains(range.commonAncestorContainer) || !range.collapsed) return;

    const { startContainer, startOffset } = range;
    const block = closestBlock(startContainer, rootEl);
    if (!block) return;

    const tailFragment = extractTailFrom(block, startContainer, startOffset);
    stripEmptyTextNodes(block);
    stripEmptyInlineElements(block);
    stripEmptyTextNodes(tailFragment);
    stripEmptyInlineElements(tailFragment);
    // A lone <br> placeholder (the empty-block convention) can only survive
    // into the tail here if the block WAS empty (nothing else to extract) —
    // real text is about to exist in this block, so the placeholder no
    // longer belongs. stripEmptyInlineElements deliberately leaves <br>
    // alone (see its own comment), so it has to be dropped explicitly here.
    Array.from(tailFragment.childNodes).forEach((node) => {
        if (node.nodeType === 1 && node.tagName === 'BR') tailFragment.removeChild(node);
    });

    const textNode = document.createTextNode(text);
    block.appendChild(wrapWithMarks(textNode, marks));
    block.appendChild(tailFragment);

    const caret = document.createRange();
    caret.setStartAfter(textNode);
    caret.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caret);
};

const LIST_WRAPPER_TAG = { bullet: 'UL', numbered: 'OL' };
const LIST_TAG_NAME = { bullet: 'ul', numbered: 'ol' };

// Plain-text character offset of (container, offset) relative to the START
// of `block`. toggleListForBlock moves the caret's block's child NODES
// as-is (never clones them) into their replacement block, so the caret's
// own DOM node identity would often survive a naive "just leave the
// selection alone" approach — except when the caret's container was the
// block element itself (an empty block, where the old block gets discarded
// entirely and the Range's boundary-point fixup has nothing sensible left
// to follow). Recomputing by offset and re-placing explicitly is correct in
// both cases, not just the tricky one.
const textOffsetInBlock = (block, container, offset) => {
    const range = document.createRange();
    range.setStart(block, 0);
    range.setEnd(container, offset);
    return range.toString().length;
};

const placeCaretAtTextOffset = (block, textOffset, selection) => {
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let remaining = textOffset;
    let target = { container: block, offset: 0 };
    let node = walker.nextNode();
    while (node) {
        if (remaining <= node.nodeValue.length) { target = { container: node, offset: remaining }; break; }
        remaining -= node.nodeValue.length;
        target = { container: node, offset: node.nodeValue.length };
        node = walker.nextNode();
    }
    const range = document.createRange();
    range.setStart(target.container, target.offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};

// --- In-editor link click popover (spec 8b, editor case) -------------------
//
// Click detection: the closest <a href> ancestor of `node`, bounded at
// `rootEl` (never walks past the editor root — a click target reached via
// portal/event delegation from somewhere else on the page must not match an
// unrelated ancestor link elsewhere in the DOM). Pure/DOM-only, no Selection
// involved, so it is fully jsdom-testable — EditableNote.jsx's onClick
// handler feeds it `event.target` directly.
export const findLinkAncestor = (node, rootEl) => {
    let el = node && node.nodeType === 3 ? node.parentNode : node;
    while (el && el !== rootEl) {
        if (el.tagName === 'A' && el.getAttribute('href')) return el;
        el = el.parentNode;
    }
    return null;
};

// Block-position of a SINGLE boundary point (container, offset), in the same
// {blockIndex, textOffset} shape EditableNote.jsx's own saveCaretPosition
// produces for a live Selection's boundaries — factored out here so a
// capture that has NO live Selection to read (the in-editor link click
// popover's Edit/Remove actions, which target a specific <a> element's full
// extent rather than whatever is currently selected) can still produce a
// snapshot EditableNote.jsx's existing restoreCaretPosition already knows
// how to restore, unchanged.
//
// Unlike a naive "walk the block's text nodes until we hit `container`"
// re-implementation (which only works when `container` IS one of those text
// nodes), this goes through `closestBlock`/`textOffsetInBlock` — both
// already correct for an ELEMENT container/offset pair too (offset = a
// child index, not a character index), which is exactly the shape
// `Range.selectNodeContents(anchorEl)` produces for its own two boundary
// points (container=anchorEl, offset 0 and offset anchorEl.childNodes.length)
// — the case `snapshotForElementContents` below feeds it.
export const blockPositionForBoundary = (rootEl, container, offset) => {
    const blockEl = closestBlock(container, rootEl);
    if (!blockEl) return null;
    const blocks = Array.from(rootEl.querySelectorAll('p, li'));
    const blockIndex = blocks.indexOf(blockEl);
    if (blockIndex === -1) return null;
    return { blockIndex, textOffset: textOffsetInBlock(blockEl, container, offset) };
};

// Selection-independent capture snapshot over an element's FULL contents, in
// the same {start, end, collapsed} shape as EditableNote.jsx's own
// getSelectionSnapshot/beginLinkTarget — built directly from `el`'s two
// boundary points (mirroring what `range.selectNodeContents(el)` would set)
// instead of the live Selection. Lets a click on linked text — which places
// a COLLAPSED caret at the click point, per spec, so there is nothing live
// to select — still capture that link's whole extent for Edit/Remove.
// `el` must be a live descendant of `rootEl`; null for anything else
// (detached element, or a block position that can't be resolved).
export const snapshotForElementContents = (rootEl, el) => {
    if (!rootEl || !el || !rootEl.contains(el)) return null;
    const start = blockPositionForBoundary(rootEl, el, 0);
    const end = blockPositionForBoundary(rootEl, el, el.childNodes.length);
    if (!start || !end) return null;
    const collapsed = start.blockIndex === end.blockIndex && start.textOffset === end.textOffset;
    return { start, end, collapsed };
};

// Spec rule 5.E: a list action at a collapsed caret toggles ONLY the
// caret's own block, never the whole note. Converting the block's tag
// correctly is enough for storage even when that leaves the live DOM with
// two back-to-back <ul>/<ol> elements instead of one merged list — the
// normalizer groups contiguous same-kind blocks into a single <ul>/<ol> at
// serialize time regardless of how many list elements the raw DOM currently
// has (see normalizeNoteHtml/serializeBlocks), so no DOM-level list-joining
// is attempted here.
export const toggleListForBlock = (rootEl, selection, kind) => {
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    if (!rootEl.contains(range.commonAncestorContainer) || !range.collapsed) return false;

    const { startContainer, startOffset } = range;
    const block = closestBlock(startContainer, rootEl);
    if (!block) return false;
    const textOffset = textOffsetInBlock(block, startContainer, startOffset);

    if (block.tagName === 'LI' && block.parentNode.tagName === LIST_WRAPPER_TAG[kind]) {
        const newP = unwrapListItemToParagraph(block);
        placeCaretAtTextOffset(newP, textOffset, selection);
        return true;
    }

    const newLi = document.createElement('li');
    Array.from(block.childNodes).forEach((node) => newLi.appendChild(node));
    if (!newLi.hasChildNodes()) newLi.appendChild(document.createElement('br'));
    const newList = document.createElement(LIST_TAG_NAME[kind]);
    newList.appendChild(newLi);

    if (block.tagName === 'LI') spliceListWithReplacement(block, newList);
    else block.parentNode.replaceChild(newList, block);

    placeCaretAtTextOffset(newLi, textOffset, selection);
    return true;
};
